import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Building2, MapPin, Users, Cog, DollarSign, TrendingDown, AlertTriangle, BarChart3, ArrowUpRight, ArrowDownRight, ShieldCheck, Clock, Package, Wrench, BookOpen } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import OnboardingWizard from './OnboardingWizard';

function CostTicker({ tickers }) {
  if (!tickers || tickers.length === 0) return null;
  return (
    <Card className="stat-card" data-testid="cost-ticker">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900 font-heading flex items-center gap-2"><DollarSign size={16} className="text-orange-600" />Estimated GET Spend by Site</h3>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold animate-pulse">Live Ticker</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {tickers.map(t => (
          <Link key={t.site_id} to={`/analytics/sites/${t.site_id}`} className="bg-slate-50 rounded-lg p-3 border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer group" data-testid={`ticker-site-${t.site_id}`}>
            <p className="text-xs font-semibold text-slate-500 truncate group-hover:text-slate-900 transition-colors">{t.name}</p>
            <p className="text-lg font-bold text-slate-900 font-heading mt-0.5">₹{t.est_daily_spend?.toLocaleString()}<span className="text-xs font-normal text-slate-400">/day</span></p>
            <p className="text-xs text-slate-400">₹{t.est_monthly_spend?.toLocaleString()}/mo</p>
          </Link>
        ))}
      </div>
    </Card>
  );
}

const PIE_COLORS = ['#0F172A', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [overview, setOverview] = useState({});
  const [savings, setSavings] = useState({});
  const [quality, setQuality] = useState({});
  const [ticker, setTicker] = useState({});
  const [recs, setRecs] = useState([]);
  const [supplierData, setSupplierData] = useState({ suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    api.get('/onboarding/status').then(res => {
      if (!res.data?.completed && !res.data?.dismissed) setShowOnboarding(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats').catch(() => ({ data: {} })),
      api.get('/analytics/overview').catch(() => ({ data: {} })),
      api.get('/analytics/savings').catch(() => ({ data: {} })),
      api.get('/analytics/data-quality').catch(() => ({ data: {} })),
      api.get('/analytics/cost-ticker').catch(() => ({ data: {} })),
      api.get('/analytics/recommendations').catch(() => ({ data: {} })),
      api.get('/analytics/supplier-comparison').catch(() => ({ data: { suppliers: [] } })),
    ]).then(([s, o, sv, q, t, r, sup]) => {
      setStats(s.data); setOverview(o.data); setSavings(sv.data); setQuality(q.data);
      setTicker(t.data); setRecs(r.data?.recommendations || []);
      setSupplierData(sup.data);
    }).finally(() => setLoading(false));
  }, []);

  const totalSpend = overview.total_spend || 0;
  const savingsPotential = savings.total_savings_potential || 0;
  const savingsPct = totalSpend > 0 ? ((savingsPotential / totalSpend) * 100).toFixed(1) : 0;

  const kpiCards = [
    { label: 'Total Spend', value: `₹${totalSpend.toLocaleString()}`, sub: 'recorded', icon: DollarSign, color: 'text-orange-600 bg-orange-50', testId: 'kpi-total-spend' },
    { label: 'Avg Cost/Hr', value: `₹${(overview.avg_cost_per_hour || 0).toFixed(2)}`, sub: 'all products', icon: Clock, color: 'text-blue-600 bg-blue-50', testId: 'kpi-avg-cost-hr' },
    { label: 'Projected Annual', value: `₹${(overview.projected_annual_spend || 0).toLocaleString()}`, sub: 'estimated', icon: TrendingDown, color: 'text-violet-600 bg-violet-50', testId: 'kpi-projected-annual' },
    { label: 'Savings Potential', value: `₹${savingsPotential.toLocaleString()}`, sub: savingsPct > 0 ? `${savingsPct}% of spend` : '', icon: ArrowDownRight, color: 'text-emerald-600 bg-emerald-50', testId: 'kpi-savings-potential' },
  ];

  const isSuperAdmin = user?.role === 'super_admin';

  const opCards = [
    ...(isSuperAdmin ? [{ label: 'Companies', value: stats.companies || 0, icon: Building2, color: 'text-blue-600 bg-blue-50' }] : []),
    { label: 'Mining Sites', value: stats.sites || 0, icon: MapPin, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Machines Tracked', value: stats.machines || 0, icon: Cog, color: 'text-amber-600 bg-amber-50' },
    { label: 'Active Cycles', value: stats.active_cycles || 0, icon: Clock, color: 'text-violet-600 bg-violet-50' },
    { label: 'Change-Outs', value: stats.closed_cycles || 0, icon: Wrench, color: 'text-slate-600 bg-slate-100' },
  ];

  const supplierPieData = (supplierData.suppliers || []).filter(s => s.total_spend > 0).slice(0, 6).map(s => ({ name: s.name, value: s.total_spend }));

  return (
    <div data-testid="dashboard-page">
      <div className="page-header flex-col sm:flex-row gap-2">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Data Quality:</span>
          <span className={`text-xs font-bold px-2 py-1 rounded ${(quality.score || 0) >= 70 ? 'bg-emerald-50 text-emerald-600' : (quality.score || 0) >= 40 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-testid="dashboard-quality-score">{quality.score || 0}/100</span>
        </div>
      </div>

      {/* Onboarding Popup for first-time users */}
      <Dialog open={showOnboarding} onOpenChange={(open) => { if (!open) setShowOnboarding(false); }}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="onboarding-dialog">
          <DialogTitle className="sr-only">DigSense IQ Setup Guide</DialogTitle>
          <OnboardingWizard isModal={true} onClose={() => setShowOnboarding(false)} />
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="stat-card animate-pulse h-28" />)}
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8" data-testid="kpi-cards">
            {kpiCards.map(({ label, value, sub, icon: Icon, color, testId }) => (
              <Card key={label} className="stat-card" data-testid={testId}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1 font-heading">{value}</p>
                    {sub && <span className="text-[10px] uppercase tracking-wider mt-1 inline-block px-1.5 py-0.5 rounded bg-slate-50 text-slate-400">{sub}</span>}
                  </div>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}><Icon size={18} strokeWidth={1.5} /></div>
                </div>
              </Card>
            ))}
          </div>

          {/* Cost Ticker */}
          <div className="mb-8">
            <CostTicker tickers={ticker.sites || []} />
          </div>

          {/* Spend Trend + Supplier Mix */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <Card className="lg:col-span-2 stat-card" data-testid="spend-trend-chart">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Monthly Spend Trend</h3>
              {(overview.spend_trend || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={overview.spend_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => { const [y, m] = (v || '').split('-'); const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${names[parseInt(m,10)-1] || m} '${(y||'').slice(-2)}`; }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="spend" fill="#0F172A" radius={[4,4,0,0]} name="Spend" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 text-center py-16">No spend data to display</p>}
            </Card>

            <Card className="stat-card" data-testid="supplier-spend-pie">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Spend by Supplier</h3>
              {supplierPieData.length > 0 ? (
                <div>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={supplierPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                        {supplierPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 mt-2">
                    {supplierPieData.map((s, i) => (
                      <div key={s.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} /><span className="text-slate-600 truncate max-w-[120px]">{s.name}</span></div>
                        <span className="font-semibold text-slate-900">₹{s.value.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-16">No supplier data</p>}
            </Card>
          </div>

          {/* Operational Overview + Savings Spotlight */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <Card className="stat-card">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Operational Summary</h3>
              <div className="space-y-3">
                {opCards.map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${color}`}><Icon size={14} /></div>
                      <span className="text-sm text-slate-600">{label}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">{value}</span>
                  </div>
                ))}
                <Separator className="my-2" />
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-md flex items-center justify-center bg-slate-100"><Package size={14} className="text-slate-600" /></div><span className="text-sm text-slate-600">Products</span></div>
                  <span className="text-sm font-bold text-slate-900">{stats.products || 0}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-md flex items-center justify-center bg-slate-100"><BarChart3 size={14} className="text-slate-600" /></div><span className="text-sm text-slate-600">Operations</span></div>
                  <span className="text-sm font-bold text-slate-900">{stats.operations || 0}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-md flex items-center justify-center bg-slate-100"><ShieldCheck size={14} className="text-slate-600" /></div><span className="text-sm text-slate-600">Proof Compliance</span></div>
                  <span className={`text-sm font-bold ${(quality.proof_compliance_rate || 0) >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>{(quality.proof_compliance_rate || 0).toFixed(0)}%</span>
                </div>
              </div>
            </Card>

            <Card className="lg:col-span-2 stat-card" data-testid="savings-spotlight">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900 font-heading">Savings Opportunities</h3>
                {savings.best_product && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 font-semibold">Best: {savings.best_product}</span>}
              </div>
              {(savings.opportunities || []).length > 0 ? (
                <div className="space-y-3">
                  {(savings.opportunities || []).slice(0, 4).map((opp, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-slate-900">{opp.current_product}</p>
                        <span className="text-sm font-bold text-emerald-600">Save ₹{opp.total_extra_spend?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>Current: <strong className="text-slate-700">₹{opp.current_cph?.toFixed(2)}/hr</strong></span>
                        <span className="text-slate-300">vs</span>
                        <span>Switch to: <strong className="text-emerald-600">{opp.recommended_product}</strong> @ ₹{opp.recommended_cph?.toFixed(2)}/hr</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
                        <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (opp.total_extra_spend / (savingsPotential || 1)) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-12">No savings opportunities identified yet</p>}
            </Card>
          </div>

          {/* Recommendations + Quality Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="stat-card" data-testid="recommendations-card">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-3">Recommendations</h3>
              {recs.length > 0 ? (
                <div className="space-y-3">
                  {recs.slice(0, 5).map((r, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <div className="flex items-start gap-2">
                        <ArrowUpRight size={14} className={`mt-0.5 flex-shrink-0 ${r.priority === 'high' ? 'text-orange-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{r.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.description}</p>
                          <span className={`text-[10px] uppercase tracking-wider mt-1 inline-block px-1.5 py-0.5 rounded ${r.confidence === 'high' ? 'bg-emerald-50 text-emerald-600' : r.confidence === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>{r.confidence} confidence</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-8">Add operational data to generate recommendations</p>}
            </Card>

            <Card className="stat-card" data-testid="quality-alerts-card">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-3">Data Quality Alerts</h3>
              {(quality.issues || []).length > 0 ? (
                <div className="space-y-2">
                  {(quality.issues || []).slice(0, 6).map((issue, i) => (
                    <div key={i} className="flex items-start gap-2 py-2 border-b border-slate-50 last:border-0">
                      <AlertTriangle size={14} className={`mt-0.5 flex-shrink-0 ${issue.severity === 'high' ? 'text-red-500' : issue.severity === 'medium' ? 'text-amber-500' : 'text-slate-400'}`} />
                      <div>
                        <p className="text-sm text-slate-700">{issue.message}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${issue.severity === 'high' ? 'bg-red-50 text-red-500' : issue.severity === 'medium' ? 'bg-amber-50 text-amber-500' : 'bg-slate-50 text-slate-400'}`}>{issue.severity}</span>
                          {issue.count && <span className="text-[10px] text-slate-400">{issue.count} records</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-8">No data quality issues detected</p>}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
